import React from 'react';
import styled from '@emotion/styled';
import { connect } from 'react-redux';
import StickyPrivate from './private';
import StickyAgent from './agent';
import StickyDeveloper from './developer';
import { makeContactMapStateToPropsFactory, ContactPoiGetter, poiSelector } from 'components/contact/makeContactMapStateToPropsFactory';
import { ContactUnionProps, ContactType } from 'components/contact/types';
import { PoiId, PocType } from 'utils/entities';
import { isBulletinPoiType, isProjectPoiType } from 'utils/marketplaceRoutes';
import withEvents from 'analytics/withEvents';
import { IContactPageSubmitVariables, IUpdateUserProfileVariables, MutationType } from 'store/sagas/apiService/types';
import { getContactPageEventPayload, getPOCPhoneNumber, getProjectPhoneNumber } from 'helpers/contact';
import { createPortal } from 'utils/universalPortal';
import { STICKY_ROOT } from 'consts/rootNodes';
import { ContactModalOpenMeta } from 'store/state/app';
import { Dispatch } from 'redux';
import { RootAction } from 'store/state';
import { setContactAgentOpen } from 'store/state/app/actions';
import { BannerSticky } from './banner-sticky';
import { inlineMutate } from 'store/state/mutationsResponse/actions';

const poiByIdsSelector: ContactPoiGetter = (state) => poiSelector(state);

type POIStickyContactEntryProps = ContactUnionProps & {
  id: PoiId;
  onSetContactModalPoiId: (id: PoiId, meta: ContactModalOpenMeta) => void;
  onCallClickEvent?: (isVisible: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
  isBannerVisible?: boolean;
};

const Root = styled.div({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 13,
  willChange: 'transform',
  transform: 'translateY(100%)',
  animation: '.2s linear 1s forwards slideUp',
  '@keyframes slideUp': {
    from: {
      transform: 'translateY(100%)',
    },
    to: {
      transform: 'translateY(0)',
    },
  },
});

const POIStickyContactEntry: React.FC<POIStickyContactEntryProps> = (props) => {
  if (!props) return null;

  let content = null;

  switch (props.cardType) {
    case ContactType.Private:
      content = <StickyPrivate {...props} />;
      break;
    case ContactType.Agent:
      content = <StickyAgent {...props} />;
      break;
    case ContactType.Developer:
      content = <StickyDeveloper {...props} />;
      break;
    default:
      content = null;
      break;
  }

  return createPortal(
    <Root data-auto="poi-sticky-contact">
      <BannerSticky isOpen={props.isBannerVisible} />
      {content}
    </Root>,
    STICKY_ROOT
  );
};

const POIStickyContactEntryWithEvents = withEvents<POIStickyContactEntryProps>((sendEvent, props) => ({
  onCallClickEvent: (isVisible: boolean, formValues: IContactPageSubmitVariables, source?: string) => {
    const contactDetails = getContactPageEventPayload(isBulletinPoiType(props.poi) && props.poi.poc, formValues, isVisible);
    const event = {
      ...contactDetails.event,
      ...(source ? { source } : null),
      ...('poc' in props.poi && props.poi.poc && props.poi.poc.type === PocType.Agent && !isVisible ? {
        agent_details: {
          id: props.poi.poc.agentId,
          office_id: props.poi.poc.officeId,
        },
      } : null),
    };

    sendEvent(isVisible ? 'property_lead_call_submit' : 'property_lead_phone_expose', 'property', {
      ...contactDetails,
      event,
      bulletinId: props.id,
      ...(isVisible ? {
        rtbData: {
          phone: isBulletinPoiType(props.poi) && getPOCPhoneNumber(props.poi.poc) || isProjectPoiType(props.poi) && getProjectPhoneNumber(props.poi),
        },
      } : null),
    });
  },
}))(POIStickyContactEntry);

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onSetContactModalPoiId: (id: PoiId, meta: ContactModalOpenMeta) => dispatch(setContactAgentOpen(id, meta)),
  onUpdateUser: (data: Pick<IUpdateUserProfileVariables, 'phone'>) => dispatch(inlineMutate(MutationType.UpdateUserProfile, data)),
});

const makeMapStateToProps = makeContactMapStateToPropsFactory(poiByIdsSelector);
const ConnectedPOIStickyContactEntry = connect(makeMapStateToProps, mapDispatchToProps)(POIStickyContactEntryWithEvents);

export default ConnectedPOIStickyContactEntry;
